import { CircularProgress, IconButton, Menu, MenuItem } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisV, faPause, faPlay, faSave } from '@fortawesome/free-solid-svg-icons';
import WaveSurfer from 'wavesurfer.js';
import Editor from 'react-simple-code-editor';
import { highlight, languages } from 'prismjs';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-typescript';
import 'prismjs/components/prism-jsx';
import 'prismjs/components/prism-tsx';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-markup-templating';
import 'prismjs/components/prism-php';
import 'prismjs/components/prism-markdown';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-sql';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-yaml';
import 'prismjs/components/prism-markup';
import 'prismjs/themes/prism.css';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useAiAssistant } from '../../AiAssistantContext';
import { ImageViewerButtons, useImageViewer } from './ImageViewerControls';
import * as api from '../../api';
import CsvEditor from './CsvEditor';
import Markdown from 'components/common/Markdown';

interface IProps {
  path: string;
}

const LANG_MAP: Record<string, string> = {
  js: 'javascript',
  jsx: 'jsx',
  ts: 'typescript',
  tsx: 'tsx',
  json: 'json',
  php: 'php',
  md: 'markdown',
  css: 'css',
  scss: 'css',
  html: 'markup',
  xml: 'markup',
  yml: 'yaml',
  yaml: 'yaml',
  sh: 'bash',
  bash: 'bash',
  sql: 'sql',
  py: 'python',
};

function getGrammar(filePath: string) {
  const ext = filePath.split('.').pop()?.toLowerCase() || '';
  const lang = LANG_MAP[ext] || 'text';
  return { grammar: languages[lang] || languages.plain || null, lang };
}

function downloadDataUrl(dataUrl: string, filename: string) {
  const [header, base64] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)?.[1] || 'application/octet-stream';
  const raw = atob(base64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  const blob = new Blob([bytes], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const FileView: React.FC<IProps> = ({ path }) => {
  const { state, dispatch } = useAiAssistant();
  const projectId = state.selectedProjectId;
  const content = state.fileContents[path];
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [remoteChanged, setRemoteChanged] = useState(false);

  const ext = path.split('.').pop()?.toLowerCase() || '';
  const isCsv = ext === 'csv';
  const isMarkdown = ext === 'md' || ext === 'mdx';
  const isImage = ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp', 'ico'].includes(ext);
  const isAudio = ['mp3', 'wav', 'ogg', 'flac', 'm4a'].includes(ext);
  const isBinary = ['zip', 'rar', '7z', 'tar', 'gz', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext);
  const imageFilename = path.split('/').pop() || 'image.png';
  const { bgColor: imageBgColor, isHovering: imageHovering, hoverProps: imageHoverProps, toggleDarkMode, handleDownload: handleImageDownload } = useImageViewer(isImage ? (content ?? null) : null, imageFilename);
  const { grammar, lang } = getGrammar(path);

  const loadContent = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError('');
    try {
      const result = await api.fetchFileContent(projectId, path);
      dispatch({ type: 'SET_FILE_CONTENT', path, content: result.content });
      setEditContent(result.content);
      setDirty(false);
    } catch {
      setError('Failed to load file');
    }
    setLoading(false);
  }, [projectId, path, dispatch]);

  useEffect(() => {
    if (content === undefined) {
      loadContent();
    } else {
      setEditContent(content);
      setDirty(false);
    }
  }, [content, loadContent]);

  // Never auto-reload on agent file changes — show a banner and let the user reload manually
  // so their reading position isn't lost.
  useEffect(() => {
    if (state.agentFileChangeSeq > 0) {
      setRemoteChanged(true);
    }
  }, [state.agentFileChangeSeq]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = useCallback(async () => {
    if (!projectId || saving) return;
    setSaving(true);
    setError('');
    try {
      await api.updateFileContent(projectId, path, editContent);
      dispatch({ type: 'SET_FILE_CONTENT', path, content: editContent });
      setDirty(false);
    } catch {
      setError('Failed to save file');
    }
    setSaving(false);
  }, [projectId, path, editContent, saving, dispatch]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSave]);

  const doDownload = useCallback(() => {
    const data = content || '';
    const filename = path.split('/').pop() || 'file';
    if (data.startsWith('data:')) {
      downloadDataUrl(data, filename);
    } else {
      const blob = new Blob([data], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  }, [content, path]);

  const handleMenuDownload = () => {
    doDownload();
    setMenuAnchor(null);
  };

  const handleChange = (value: string) => {
    setEditContent(value);
    setDirty(value !== (content || ''));
  };

  const handleReload = () => {
    setRemoteChanged(false);
    loadContent();
  };

  const handleDismissRemoteChange = () => {
    setRemoteChanged(false);
  };

  if (loading) {
    return (
      <CenterText>
        <CircularProgress size={24} />
      </CenterText>
    );
  }

  return (
    <Container>
      <Toolbar>
        <ToolbarLeft>
          <FileName>
            {path}
            {dirty && <DirtyDot title="Unsaved changes" />}
          </FileName>
          {!isImage && !isAudio && !isBinary && (
            <ModeToggle>
              <ModeBtn $active={mode === 'view'} onClick={() => setMode('view')}>View</ModeBtn>
              <ModeBtn $active={mode === 'edit'} onClick={() => setMode('edit')}>Edit</ModeBtn>
            </ModeToggle>
          )}
        </ToolbarLeft>
        <ToolbarActions>
          {mode === 'edit' && !isImage && !isAudio && (
            saving ? (
              <IconButton size="small" disabled>
                <CircularProgress size={14} />
              </IconButton>
            ) : (
              <IconButton size="small" onClick={handleSave} disabled={!dirty} title="Save (Ctrl+S)">
                <FontAwesomeIcon icon={faSave} size="xs" style={{ color: dirty ? '#1976d2' : undefined }} />
              </IconButton>
            )
          )}
          <>
            <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
              <FontAwesomeIcon icon={faEllipsisV} size="xs" />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
            >
              <MenuItem onClick={handleMenuDownload}>Download</MenuItem>
            </Menu>
          </>
        </ToolbarActions>
      </Toolbar>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      {remoteChanged && (
        <WarningBanner>
          <span>This file was modified by the agent.</span>
          <BannerActions>
            <BannerBtn onClick={handleReload}>Reload</BannerBtn>
            <BannerBtn onClick={handleDismissRemoteChange}>Dismiss</BannerBtn>
          </BannerActions>
        </WarningBanner>
      )}
      <EditorArea>
        {isBinary ? (
          <BinaryView>
            <BinaryLabel>{path.split('/').pop()}</BinaryLabel>
            <BinaryDownloadBtn onClick={doDownload}>Download file</BinaryDownloadBtn>
          </BinaryView>
        ) : isAudio ? (
          <AudioView>
            <AudioPlayer src={content || ''} />
          </AudioView>
        ) : isImage ? (
          <ImageView>
            <ImageBox $bgColor={imageBgColor} {...imageHoverProps}>
              <img src={content || ''} alt={path} />
              {imageHovering && (
                <ImageViewerButtons onDownload={handleImageDownload} onToggleDark={toggleDarkMode} size={32} offset={12} />
              )}
            </ImageBox>
          </ImageView>
        ) : mode === 'view' ? (
          isCsv ? (
            <CsvEditor value={content || ''} readOnly />
          ) : isMarkdown ? (
            <MarkdownView>
              <Markdown>{content || ''}</Markdown>
            </MarkdownView>
          ) : (
            <CodeView
              dangerouslySetInnerHTML={{
                __html: grammar
                  ? (() => { try { return highlight(content || '', grammar, lang); } catch { return content || ''; } })()
                  : content || '',
              }}
            />
          )
        ) : (
          isCsv ? (
            <CsvEditor value={editContent} onChange={handleChange} />
          ) : (
            <EditorWithLines>
              {!isMarkdown && (
                <LineNumbers>
                  {editContent.split('\n').map((_, i) => (
                    <span key={i}>{i + 1}</span>
                  ))}
                </LineNumbers>
              )}
              <StyledEditor
                value={editContent}
                onValueChange={handleChange}
                highlight={(code) => {
                  if (!grammar) return code;
                  try { return highlight(code, grammar, lang); } catch { return code; }
                }}
                padding={16}
                textareaId="file-editor"
                $wrap={isMarkdown}
              />
            </EditorWithLines>
          )
        )}
      </EditorArea>
    </Container>
  );
};

export default FileView;

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 16px;
  border-bottom: 1px solid #eee;
`;

const ToolbarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ModeToggle = styled.div`
  display: flex;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
`;

const ModeBtn = styled.button<{ $active: boolean }>`
  border: none;
  background: ${(p) => (p.$active ? '#1976d2' : 'transparent')};
  color: ${(p) => (p.$active ? '#fff' : '#666')};
  font-size: 11px;
  font-weight: 500;
  padding: 2px 10px;
  cursor: pointer;
  &:hover {
    background: ${(p) => (p.$active ? '#1976d2' : '#f0f0f0')};
  }
`;

const FileName = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #555;
`;

const DirtyDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ff9800;
  display: inline-block;
`;

const ToolbarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const EditorArea = styled.div`
  flex: 1;
  overflow: auto;
  background: #fafafa;
`;

const AudioPlayer: React.FC<{ src: string }> = ({ src }) => {
  const waveformRef = useRef<HTMLDivElement>(null);
  const waveSurferRef = useRef<WaveSurfer>();
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!waveformRef.current || !src) return;

    const ws = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: '#90a4ae',
      progressColor: '#1976d2',
      cursorColor: '#1976d2',
      height: 64,
      barWidth: 2,
      barGap: 1,
      responsive: true,
    });

    ws.load(src);
    ws.on('ready', () => setReady(true));
    ws.on('finish', () => setPlaying(false));
    waveSurferRef.current = ws;

    return () => {
      ws.destroy();
      waveSurferRef.current = undefined;
    };
  }, [src]);

  const togglePlay = () => {
    if (!waveSurferRef.current) return;
    waveSurferRef.current.playPause();
    setPlaying((p) => !p);
  };

  return (
    <AudioPlayerWrap>
      <PlayButton onClick={togglePlay} disabled={!ready}>
        <FontAwesomeIcon icon={playing ? faPause : faPlay} />
      </PlayButton>
      <WaveformWrap ref={waveformRef} />
    </AudioPlayerWrap>
  );
};

const AudioPlayerWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 0 8px;
`;

const PlayButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: #1976d2;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 13px;
  &:disabled {
    background: #ccc;
    cursor: default;
  }
  &:not(:disabled):hover {
    background: #1565c0;
  }
`;

const WaveformWrap = styled.div`
  flex: 1;
  min-width: 0;
  background: #f5f5f5;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  padding: 4px 8px;
  overflow: hidden;
`;

const BinaryView = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 24px;
  color: #666;
`;

const BinaryLabel = styled.div`
  font-size: 15px;
  font-weight: 500;
`;

const BinaryDownloadBtn = styled.button`
  padding: 8px 20px;
  background: #1976d2;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  &:hover { background: #1565c0; }
`;

const AudioView = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  padding: 24px;
`;

const ImageView = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const ImageBox = styled.div<{ $bgColor: string }>`
  position: relative;
  display: inline-block;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: ${(p) => p.$bgColor};
  transition: background-color 0.2s ease;
  overflow: hidden;
  img {
    display: block;
    max-width: calc(100vw - 80px);
    max-height: calc(100vh - 160px);
    object-fit: contain;
  }
`;


const CodeView = styled.pre`
  margin: 0;
  padding: 16px;
  font-family: ui-monospace, SFMono-Regular, Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const MarkdownView = styled.div`
  padding: 16px 24px;
  font-size: 14px;
  line-height: 1.6;
  table {
    border-collapse: collapse;
    margin: 12px 0;
    width: auto;
  }
  th, td {
    border: 1px solid #ddd;
    padding: 6px 12px;
    text-align: left;
  }
  th {
    background: #f5f5f5;
    font-weight: 600;
  }
  h1, h2, h3, h4 { margin: 16px 0 8px; }
  p { margin: 8px 0; }
  code {
    background: #f0f0f0;
    padding: 1px 4px;
    border-radius: 3px;
    font-size: 13px;
  }
  pre {
    background: #f5f5f5;
    padding: 12px;
    border-radius: 4px;
    overflow-x: auto;
  }
  pre code {
    background: none;
    padding: 0;
  }
`;

const EditorWithLines = styled.div`
  display: flex;
  min-height: 100%;
`;

const LineNumbers = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 0;
  min-width: 40px;
  text-align: right;
  font-family: ui-monospace, SFMono-Regular, Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 13px;
  line-height: 1.5;
  color: #aaa;
  user-select: none;
  border-right: 1px solid #e0e0e0;
  & > span {
    padding: 0 8px;
  }
`;

const StyledEditor = styled(Editor)<{ $wrap?: boolean }>`
  flex: 1;
  min-height: 100%;
  font-family: ui-monospace, SFMono-Regular, Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 13px;
  line-height: 1.5;
  & textarea, & pre {
    white-space: ${(p) => (p.$wrap ? 'pre-wrap' : 'pre')} !important;
    ${(p) => p.$wrap && 'word-break: break-word !important;'}
  }
  & textarea {
    outline: none !important;
  }
`;

const ErrorBanner = styled.div`
  padding: 6px 16px;
  background: #ffebee;
  color: #c62828;
  font-size: 13px;
  border-bottom: 1px solid #ffcdd2;
`;

const WarningBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 16px;
  background: #fff3e0;
  color: #e65100;
  font-size: 13px;
  border-bottom: 1px solid #ffe0b2;
`;

const BannerActions = styled.div`
  display: flex;
  gap: 8px;
`;

const BannerBtn = styled.button`
  border: none;
  background: none;
  color: #e65100;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 2px 8px;
  border-radius: 3px;
  &:hover {
    background: rgba(230, 81, 0, 0.1);
  }
`;

const CenterText = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
`;
